import { Canvas } from 'fabric'
import { useState, useEffect, useMemo, useRef } from 'react'

import { useLocalStorage } from './useLocalStorage'
import { TEMPLATE_BUILDER_GLOBAL_SNAPSHOT_LS_KEY } from '../constants'

export const useCanvasHistoryStack = (canvas: Canvas | null) => {
  const [pageStackSnapshot, setPageStackSnapshot] = useState<any>([])
  const [historyStack, setHistoryStack] = useState<any>([])
  const [stackCursor, setStackCursor] = useState<number>(-1)
  const [currentWorkingPage, setCurrentWorkingPage] = useState<number>(0)
  const { setLocalStorage, getLocalStorage } = useLocalStorage()
  const isProcessingHistory = useRef(false)

  useEffect(() => {
    if (!canvas) return
    if (!historyStack) {
      return
    }
    const isUndoState = stackCursor < historyStack.length - 1

    isUndoState && saveState(canvas, currentWorkingPage, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyStack])

  const loadSnapshotFromLocalStorage = async () => {
    const snapshot = await getLocalStorage(
      TEMPLATE_BUILDER_GLOBAL_SNAPSHOT_LS_KEY,
    )

    if (snapshot) {
      const firstSnapshot = snapshot[0]
      await setPageStackSnapshot(snapshot)
      await setStackCursor(firstSnapshot.stackCursor)
      await setHistoryStack(firstSnapshot.snapshots)
    }
  }

  useEffect(() => {
    const handleBeforeUnload = () => {
      setLocalStorage(
        TEMPLATE_BUILDER_GLOBAL_SNAPSHOT_LS_KEY,
        pageStackSnapshot,
      )
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageStackSnapshot])

  const saveState = async (
    newCanvas: Canvas | null | undefined,
    activePage: number,
    shouldClearFuture?: boolean,
  ) => {
    if (!newCanvas) return
    setCurrentWorkingPage(activePage)
    const state = await newCanvas.toObject([
      'customId',
      'customVariableKey',
      'customObjectType',
      'customObjectAlign',
      'customObjectWidth',
      'selectable',
      'evented',
      'lockMovementX',
      'lockMovementY',
      'lockRotation',
      'lockScalingX',
      'lockScalingY',
      'lockScalingFlip',
      'hasControls',
      'hasBorders',
    ])

    setHistoryStack((prev: any) => {
      if (shouldClearFuture) {
        const newStack = prev.slice(0, stackCursor + 1)
        updatePageStackSnapshot(activePage, [...newStack, state])
        return [...newStack, state]
      } else {
        updatePageStackSnapshot(activePage, [...prev, state])
        return [...prev, state]
      }
    })

    setStackCursor((prev) => {
      if (shouldClearFuture) {
        return stackCursor + 1
      } else {
        return prev + 1
      }
    })
  }

  const undo = async () => {
    if (!canvas || stackCursor <= 0) return
    canvas.discardActiveObject()

    const undoState = historyStack[stackCursor - 1]
    if (undoState) {
      isProcessingHistory.current = true
      try {
        await canvas.loadFromJSON(historyStack[stackCursor - 1])
        await canvas?.renderAll()
        setStackCursor((prev) => prev - 1)
      } finally {
        isProcessingHistory.current = false
      }
    }
  }

  const redo = async () => {
    if (!canvas || stackCursor >= historyStack.length - 1) return
    canvas.discardActiveObject()
    const redoState = historyStack[stackCursor + 1]
    if (redoState) {
      isProcessingHistory.current = true
      try {
        await canvas.loadFromJSON(redoState)
        await canvas.renderAll()
        setStackCursor((prev) => prev + 1)
      } finally {
        isProcessingHistory.current = false
      }
    }
  }

  const updateTabHistoryStack = async (
    oldPageIndex: number,
    newPageIndex: number,
  ) => {
    const currentSnapshotList = pageStackSnapshot

    if (!currentSnapshotList[oldPageIndex]) {
      currentSnapshotList[oldPageIndex] = {
        stackCursor,
        snapshots: historyStack,
      }
    }

    const beforeChangeStackSnapshot = {
      stackCursor,
      snapshots: historyStack,
    }

    const loadedSnapshot =
      currentSnapshotList[newPageIndex] &&
      JSON.parse(JSON.stringify(currentSnapshotList[newPageIndex]))
    currentSnapshotList[oldPageIndex] = beforeChangeStackSnapshot

    await setPageStackSnapshot(currentSnapshotList)

    if (loadedSnapshot) {
      isProcessingHistory.current = true
      try {
        await setStackCursor(loadedSnapshot.stackCursor)
        await setHistoryStack(loadedSnapshot.snapshots)
        await canvas?.renderAll()
      } finally {
        isProcessingHistory.current = false
      }
    }
  }

  const updatePageStackSnapshot = async (
    pageIndex: number,
    newHistoryStack: any,
  ) => {
    const currentSnapshotList = pageStackSnapshot

    currentSnapshotList[pageIndex] = {
      stackCursor: newHistoryStack.length - 1,
      snapshots: newHistoryStack,
    }

    await setPageStackSnapshot(currentSnapshotList)
  }

  const deletePageStackSnapshot = async (pageIndex: number) => {
    const currentSnapshotList = pageStackSnapshot

    currentSnapshotList.splice(pageIndex, 1)

    await setPageStackSnapshot(currentSnapshotList)
  }

  const currentStackObj = useMemo(() => {
    return historyStack[stackCursor]
  }, [stackCursor, historyStack])

  return {
    saveState,
    undo,
    redo,
    stackCursor,
    historyStack,
    updateTabHistoryStack,
    loadSnapshotFromLocalStorage,
    deletePageStackSnapshot,
    currentStackObj,
    updatePageStackSnapshot,
    isProcessingHistory,
  }
}
